import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import billingService from "../../services/billingService";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MiPlanCard from "../perfil/MiPlanCard";
import usageService from "../../services/usageService";

const CONTACT_EMAIL = "contact@voras.com";

// Catálogo de planes (espeja la landing). annual = equivalente mensual con 20% off.
const PLANS = [
  { key: "Free", name: "Free", monthly: 0, annual: 0, egresos: 79 },
  { key: "Starter", name: "Starter", monthly: 9, annual: 7.2, egresos: 699 },
  { key: "Standard", name: "Standard", monthly: 16, annual: 12.8, egresos: 1499 },
  { key: "Professional", name: "Professional", monthly: 28, annual: 22.4, egresos: 3299 },
  { key: "Enterprise", name: "Enterprise", custom: true, egresos: 3300, enterprise: true },
];

const fmt = (n) =>
  Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2).replace(".", ",")}`;

const Planes = () => {
  const [cycle, setCycle] = useState("monthly");
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    const userId = Number(sessionStorage.getItem("User"));
    if (!userId) return;
    usageService
      .getEgresoUsage(userId)
      .then((u) => setCurrentPlan(u?.plan ?? null))
      .catch(() => {});
  }, []);

  const annual = cycle === "annual";

  const mailtoFor = (planName) =>
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Plan Voras")}` +
    `&body=${encodeURIComponent(
      `Hola, quiero contratar/cambiar al plan ${planName} (${annual ? "anual" : "mensual"}) de Voras.`
    )}`;

  return (
    <Box sx={{ px: 5, py: 3 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: "#1a1a1a",
          pl: 1.5,
          mb: 3,
          borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
          lineHeight: 1.5,
        }}
      >
        Planes
      </Typography>

      {/* Plan actual + consumo */}
      <Box sx={{ mb: 3 }}>
        <MiPlanCard />
      </Box>

      {/* Toggle mensual / anual */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <ToggleButtonGroup
          value={cycle}
          exclusive
          onChange={(_, v) => v && setCycle(v)}
          size="small"
          color="primary"
        >
          <ToggleButton value="monthly" sx={{ textTransform: "none", px: 3 }}>
            Mensual
          </ToggleButton>
          <ToggleButton value="annual" sx={{ textTransform: "none", px: 3, gap: 1 }}>
            Anual
            <Chip
              label="-20%"
              size="small"
              color="success"
              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Grilla de planes */}
      <Grid container spacing={2.5} justifyContent="center">
        {PLANS.map((plan) => {
          const isCurrent =
            currentPlan && currentPlan.toLowerCase() === plan.key.toLowerCase();
          const priceLabel = plan.custom
            ? "A medida"
            : annual
              ? fmt(plan.annual)
              : fmt(plan.monthly);
          return (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={plan.key}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: (theme) =>
                    isCurrent
                      ? `2px solid ${theme.palette.secondary.main}`
                      : "1px solid #e0e0e0",
                  p: 2.5,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {isCurrent && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Tu plan"
                    size="small"
                    color="primary"
                    sx={{ position: "absolute", top: 12, right: 12, fontWeight: 600 }}
                  />
                )}
                <Chip
                  label={plan.name.toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: "primary.50",
                    color: "primary.main",
                    fontWeight: 700,
                    letterSpacing: 1,
                    mt: 1,
                  }}
                />
                <Typography variant="h4" fontWeight={700} sx={{ mt: 2 }}>
                  {priceLabel}
                  {!plan.custom && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {" "}
                      *
                    </Typography>
                  )}
                </Typography>
                {!plan.custom && (
                  <Typography variant="caption" color="text.secondary">
                    {annual ? "por mes · facturado anual" : "por mes"}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 2, flexGrow: 1 }}
                >
                  Incluye todas las funcionalidades.
                  <br />
                  {plan.enterprise ? "Desde " : "Hasta "}
                  <strong>{plan.egresos}</strong> egresos por mes.
                </Typography>
                {!plan.custom && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                    *USD
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant={isCurrent ? "outlined" : "contained"}
                  disabled={isCurrent}
                  // Enterprise navega a la página de contacto: como es una URL,
                  // va como <a> real (no window.location desde onClick) para no
                  // romper ctrl+click ni el anuncio de destino en lectores de pantalla.
                  {...(plan.custom && !isCurrent
                    ? { component: RouterLink, to: "/contacto-ventas" }
                    : {})}
                  onClick={
                    plan.custom
                      ? undefined
                      : async () => {
                          // Free va por contacto; los planes pagos van a Stripe
                          // Checkout. Si Stripe no está configurado → mailto.
                          if (plan.name === "Free") {
                            window.location.href = mailtoFor(plan.name);
                            return;
                          }
                          try {
                            const url = await billingService.checkout(
                              plan.name,
                              annual ? "year" : "month"
                            );
                            if (url) window.location.href = url;
                          } catch (error) {
                            window.location.href = mailtoFor(plan.name);
                          }
                        }
                  }
                  sx={{ textTransform: "none", borderRadius: 2, mt: "auto" }}
                >
                  {isCurrent
                    ? "Plan actual"
                    : plan.custom
                    ? "Hablar con un especialista"
                    : "Elegir"}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {/* Precios netos: el impuesto (IVA/percepciones) lo agrega el país o la
          tarjeta del cliente, no Voras. Misma leyenda que la landing y los T&C. */}
      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{ display: "block", mt: 3, mx: "auto", maxWidth: 640 }}
      >
        <strong>Precios en USD, netos de impuestos.</strong> Según tu país o medio de
        pago, tu banco o el emisor de tu tarjeta pueden aplicar IVA, percepciones u
        otros cargos sobre compras en el exterior.
      </Typography>
    </Box>
  );
};

export default Planes;
